import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bot, RefreshCw, X } from 'lucide-react';
import { streamOpenAIResponse } from '../../lib/openai';

interface AIResponderProps {
  onSelectResponse: (response: string) => void;
  messageContext?: string;
  isVisible: boolean;
  onClose: () => void;
}

const AIResponder: React.FC<AIResponderProps> = ({ 
  onSelectResponse, 
  messageContext = '', 
  isVisible,
  onClose
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedResponse, setGeneratedResponse] = useState('');
  const [error, setError] = useState<string | null>(null);

  const generateResponse = async () => {
    setIsGenerating(true);
    setError(null);
    setGeneratedResponse('');

    try {
      let responseText = '';
      
      const prompt = messageContext 
        ? `Generate a friendly, professional response to this message: "${messageContext}"`
        : 'Generate a friendly, professional message that could be used in a business conversation. Keep it brief (1-2 sentences).';

      await streamOpenAIResponse(
        [{ role: 'user', content: prompt }],
        (chunk) => {
          responseText += chunk;
          setGeneratedResponse(responseText);
        },
        (error) => {
          setError(error.message);
        }
      );
    } catch (err) {
      setError('Failed to generate response. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Generate a response when the component becomes visible
  React.useEffect(() => {
    if (isVisible) {
      generateResponse();
    } else {
      setGeneratedResponse('');
      setError(null);
    }
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
      className="absolute bottom-full mb-2 left-0 right-0 bg-white dark:bg-dark-800 rounded-lg shadow-lg border border-gray-200 dark:border-dark-700 p-4 z-10"
    >
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center">
          <Bot className="h-5 w-5 text-indigo-600 dark:text-indigo-400 mr-2" />
          <h3 className="text-sm font-medium text-gray-900 dark:text-white">AI Suggestion</h3>
        </div>
        <button 
          onClick={onClose}
          className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {error ? (
        <div className="text-red-500 dark:text-red-400 text-sm mb-3">
          {error}
        </div>
      ) : (
        <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-3 mb-3">
          <p className="text-gray-800 dark:text-gray-200 text-sm">
            {isGenerating ? (
              <span className="flex items-center">
                <span className="inline-block w-2 h-2 bg-indigo-600 dark:bg-indigo-400 rounded-full mr-1 animate-pulse"></span>
                <span className="inline-block w-2 h-2 bg-indigo-600 dark:bg-indigo-400 rounded-full mr-1 animate-pulse delay-150"></span>
                <span className="inline-block w-2 h-2 bg-indigo-600 dark:bg-indigo-400 rounded-full animate-pulse delay-300"></span>
                <span className="ml-2">Generating response...</span>
              </span>
            ) : generatedResponse ? (
              generatedResponse
            ) : (
              'Generating a response for you...'
            )}
          </p>
        </div>
      )}

      <div className="flex justify-between">
        <button
          onClick={generateResponse}
          disabled={isGenerating}
          className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 flex items-center"
        >
          <RefreshCw className={`h-4 w-4 mr-1 ${isGenerating ? 'animate-spin' : ''}`} />
          {isGenerating ? 'Generating...' : 'Regenerate'}
        </button>
        
        <button
          onClick={() => onSelectResponse(generatedResponse)}
          disabled={isGenerating || !generatedResponse}
          className={`px-3 py-1 text-sm rounded-lg ${
            isGenerating || !generatedResponse
              ? 'bg-gray-200 dark:bg-dark-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
              : 'bg-indigo-600 text-white hover:bg-indigo-700'
          }`}
        >
          Use Response
        </button>
      </div>
    </motion.div>
  );
};

export default AIResponder;